import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

const CLASS_COLORS: Record<string, string> = {
  claude_exhausted: '#ef4444',
  push_failure: '#f97316',
  empty_pr: '#a855f7',
  test_build_failure: '#eab308',
  kimi_crash: '#ec4899',
  kimi_conflicts: '#f59e0b',
  multi_repo_spec: '#6366f1',
  already_merged: '#22c55e',
  no_repo_mapping: '#6b7280',
  agent_type_mismatch: '#8b5cf6',
  review_changes_requested: '#0ea5e9',
  worktree_error: '#d946ef',
  network_failure: '#78716c',
  unknown: '#9ca3af',
};

const CLASS_LABELS: Record<string, string> = {
  claude_exhausted: 'Claude exhausted',
  push_failure: 'Push failure',
  empty_pr: 'Empty PR',
  test_build_failure: 'Test/build failure',
  kimi_crash: 'Kimi crash',
  kimi_conflicts: 'Kimi conflicts',
  multi_repo_spec: 'Multi-repo spec',
  already_merged: 'Already merged',
  no_repo_mapping: 'No repo mapping',
  agent_type_mismatch: 'Agent type mismatch',
  review_changes_requested: 'Review changes requested',
  worktree_error: 'Worktree error',
  network_failure: 'Network failure',
  unknown: 'Unknown',
};

interface WindowData {
  start: string;
  end: string;
  classes: Record<string, number>;
}

interface TimelineResponse {
  granularity: string;
  windows: WindowData[];
  totals: Record<string, number>;
}

interface ChartRow {
  date: string;
  [key: string]: number | string;
}

function formatDateLabel(iso: string, granularity: string): string {
  if (granularity === 'hour') {
    // "2026-05-31T14:00:00Z" → "05-31 14:00"
    return iso.slice(5, 16).replace('T', ' ');
  }
  // "2026-05-31T00:00:00Z" → "05-31"
  return iso.slice(5, 10);
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-xs">
      <div className="font-semibold text-gray-700 mb-1">{label}</div>
      {payload
        .filter((entry: any) => entry.value > 0)
        .sort((a: any, b: any) => b.value - a.value)
        .map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              {CLASS_LABELS[entry.dataKey] ?? entry.dataKey}
            </span>
            <span className="font-mono font-medium text-gray-800">{entry.value}</span>
          </div>
        ))}
    </div>
  );
}

const RANGE_BUTTONS = ['24h', '7d', '30d'] as const;
type Range = typeof RANGE_BUTTONS[number];

function ChartHeader({
  range,
  onRangeChange,
}: {
  range: Range;
  onRangeChange: (r: Range) => void;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Blocked tickets over time</h3>
      <div className="flex gap-1">
        {RANGE_BUTTONS.map((p) => (
          <button
            key={p}
            onClick={() => onRangeChange(p)}
            className={`px-2 py-0.5 text-xs rounded ${
              range === p
                ? 'bg-indigo-100 text-indigo-700 font-semibold'
                : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function BlockTimelineChart() {
  const [data, setData] = useState<TimelineResponse | null>(null);
  const [range, setRange] = useState<Range>('7d');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const res = await fetch(`/api/block-stats/timeline?range=${range}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: TimelineResponse = await res.json();
        if (!cancelled) {
          setData(json);
          setError(null);
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    }
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [range]);

  if (error) {
    return (
      <div>
        <ChartHeader range={range} onRangeChange={(r) => { setRange(r); setLoading(true); }} />
        <div className="text-red-400 text-sm">Block timeline: {error}</div>
      </div>
    );
  }

  // Build cumulative chart data from per-window raw counts (matching the
  // CompletionChart pattern). Each failure class accumulates a running total
  // so lines are monotonically non-decreasing step functions.
  const granularity = data?.granularity ?? 'day';
  const allClasses = new Set<string>(
    data
      ? [
          ...data.windows.flatMap((w) => Object.keys(w.classes)),
          ...Object.keys(data.totals),
        ]
      : []
  );

  const sortedWindows = data
    ? [...data.windows].sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      )
    : [];

  const chartData: ChartRow[] = [];
  const cumulative: Record<string, number> = {};
  for (const w of sortedWindows) {
    const row: ChartRow = { date: formatDateLabel(w.start, granularity) };
    for (const cls of allClasses) {
      // Maintain a running cumulative total per failure class; do NOT plot
      // raw per-window counts directly (CHO-307 review).
      cumulative[cls] = (cumulative[cls] ?? 0) + (w.classes[cls] ?? 0);
      row[cls] = cumulative[cls];
    }
    chartData.push(row);
  }

  const sortedClasses = [...allClasses].sort();
  const hasEvents = chartData.length > 0 && sortedClasses.length > 0;

  return (
    <div>
      <ChartHeader range={range} onRangeChange={(r) => { setRange(r); setLoading(true); }} />

      <div className="relative h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 15, right: 10, left: 30, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              width={60}
              label={{ value: 'Cumulative block count', angle: -90, position: 'left', fill: '#9CA3AF', fontSize: 11 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              iconType="circle"
              iconSize={8}
              onClick={(e: any) => {
                const key = e?.dataKey as string | undefined;
                if (!key) return;
                setHidden((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
              formatter={(value: string, _entry: any, index: number) => {
                const key = sortedClasses[index];
                const isHidden = key && hidden.has(key);
                return <span style={{ color: isHidden ? '#9ca3af' : '#374151', opacity: isHidden ? 0.6 : 1 }}>{value}</span>;
              }}
            />
            {sortedClasses.map((cls) => (
              <Line
                key={cls}
                type="stepAfter"
                dataKey={cls}
                name={CLASS_LABELS[cls] ?? cls}
                stroke={CLASS_COLORS[cls] ?? '#9ca3af'}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive={false}
                hide={hidden.has(cls)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {!hasEvents && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm bg-white/80">
            {loading ? 'Loading block timeline...' : 'No block events recorded yet.'}
          </div>
        )}
      </div>
    </div>
  );
}
